/**
 * Save a document artifact to the user's machine as a real file.
 *
 * The Reader's old "Download" did `window.open(url)`, which is popup-blocker
 * prone and — because the browser names a downloaded tab from the URL, not the
 * document — saved files with opaque internal names (or, for canvas notes with
 * no original bytes, opened a dead tab). This fetches the bytes same-origin
 * (cookie-authenticated, with the established one-shot 401 → refresh → retry)
 * and saves them via an `<a download>` named after the DOCUMENT TITLE.
 */
import { GraphMindApiError } from "@/lib/api";
import { ensureFreshToken, forceRefresh } from "@/lib/auth-token";

/** Common MIME → extension (leading dot). Covers everything the ingester
 * accepts plus the PDF/markdown export artifacts. */
const MIME_EXT: Record<string, string> = {
  "application/pdf": ".pdf",
  "text/markdown": ".md",
  "text/x-markdown": ".md",
  "text/plain": ".txt",
  "text/html": ".html",
  "application/xhtml+xml": ".html",
  "application/json": ".json",
  "text/csv": ".csv",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
  "application/vnd.ms-powerpoint": ".ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
};

/** Extension for a MIME type (ignoring any `; charset=…` suffix), or undefined. */
export function extFromMime(mime: string | null | undefined): string | undefined {
  const m = (mime ?? "").split(";")[0]!.trim().toLowerCase();
  return MIME_EXT[m];
}

/** Extension pulled off a stored path / source ref (e.g. `…/_notes.md` → `.md`),
 * or undefined. Lower-cased; only accepts a short alphanumeric suffix. */
export function extFromPath(ref: string | null | undefined): string | undefined {
  const m = /\.([a-z0-9]{1,8})$/i.exec((ref ?? "").trim());
  return m ? `.${m[1]!.toLowerCase()}` : undefined;
}

/** Best-guess extension for a document's ORIGINAL bytes from its mime, then its
 * source filename. Undefined when neither is decisive — the caller can then let
 * the response's own Content-Type decide (see {@link downloadUrlAsFile}). */
export function extensionFor(
  mime: string | null | undefined,
  sourceRef?: string | null,
): string | undefined {
  return extFromMime(mime) ?? extFromPath(sourceRef);
}

/** Turn a document title into a safe filename BASE (no extension): strip the
 * characters illegal on Windows/most filesystems and control chars, collapse
 * whitespace, drop leading dots (no hidden/".."/"." names), and cap the length.
 * Falls back to `fallback` when nothing usable remains. */
export function sanitizeFilename(name: string | null | undefined, fallback = "document"): string {
  const cleaned = (name ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[/\\:*?"<>|\u0000-\u001f]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\.+/, "")
    .trim()
    .slice(0, 120)
    .trim();
  return cleaned || fallback;
}

/**
 * Save an in-memory Blob to the user's machine as exactly `filename` via a
 * transient `<a download>` — the same mechanism {@link downloadUrlAsFile} uses,
 * minus the network fetch. For client-side artifacts we already hold in memory
 * (e.g. a note's live markdown text), where there's no URL to fetch.
 */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on the next tick so the click's navigation has consumed the URL.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

/**
 * Fetch a same-origin, cookie-authenticated URL and save its bytes as a file
 * named `filenameBase` + an extension. The extension is `preferredExt` when
 * given (the caller knows the branch — e.g. `.pdf` for a rendered snapshot),
 * otherwise derived from the response's Content-Type, otherwise `.bin`.
 *
 * Downloads as a BLOB (never `window.open`) so it can't be popup-blocked and the
 * saved file is named after the document. One 401 → `forceRefresh()` → retry,
 * the same cold-auth recovery pdf.js/`/file` use. Throws a `GraphMindApiError`
 * on a non-OK response so callers can route it through `notifyError`.
 */
export async function downloadUrlAsFile(
  url: string,
  filenameBase: string,
  preferredExt?: string,
): Promise<void> {
  await ensureFreshToken();
  // B RULE-006 exception (both call sites), removed by remediation item 4.1d.
  // This reads an arbitrary absolute URL and wants the raw Response so it can
  // take the blob and the Content-Type header; `api.request` parses JSON. 4.1d
  // gives the client a bytes-returning entry point and routes both through it.
  // eslint-disable-next-line no-restricted-globals
  let res = await fetch(url, { credentials: "include" });
  if (res.status === 401) {
    await forceRefresh();
    // eslint-disable-next-line no-restricted-globals -- B RULE-006, item 4.1d
    res = await fetch(url, { credentials: "include" });
  }
  if (!res.ok) throw new GraphMindApiError(res.status, await res.text().catch(() => ""));

  const blob = await res.blob();
  const ext =
    preferredExt ?? extFromMime(res.headers.get("content-type")) ?? extFromMime(blob.type) ?? ".bin";
  saveBlobAsFile(blob, `${filenameBase}${ext}`);
}

/**
 * Download a rendered-artifact POST response (`{ file_url, filename }`, from the
 * annotated-/compiled-PDF endpoints) as a real file. The server's `filename`
 * already carries the right extension (e.g. `"Paper-annotated.pdf"`); split it
 * into a base + ext for {@link downloadUrlAsFile} (which re-appends the ext),
 * sanitizing the base defensively. Falls back to `fallbackBase`.pdf when the
 * response omits a filename.
 */
export async function downloadArtifact(
  artifact: { file_url: string; filename?: string | null },
  fallbackBase: string,
): Promise<void> {
  const name = (artifact.filename ?? "").trim();
  const ext = extFromPath(name) ?? ".pdf";
  const base = name
    ? sanitizeFilename(name.slice(0, name.length - ext.length), fallbackBase)
    : fallbackBase;
  await downloadUrlAsFile(artifact.file_url, base, ext);
}
